import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppInputPassword from '@nxsys/components/AppInputPassword';
import AppRadioGroup from '@nxsys/components/AppRadioGroup';
import AppRadioReversed from '@nxsys/components/AppRadioReversed';
import { yesNoOptions } from '@nxsys/constants/AppConstants';
import { Form, Row, Col } from 'antd';
import { FormProps } from 'antd/lib';
import clsx from 'clsx';
import { Ref, useEffect, useImperativeHandle } from 'react';
import { useSelector } from 'react-redux';
import { AppState } from '@nxsys/store';
import IconWrapper from './IconWrapper';
import StepTitle from './StepTitle';
import AppRateInput from '@nxsys/components/AppRateInput';

export enum EmailSettingType {
  DEFAULT = 'DEFAULT',
  CUSTOM = 'CUSTOM',
}

export type EmailSettingValues = {
  emailSettingType: EmailSettingType;
  senderEmailId?: string;
  smtpHost?: string;
  smtpPort?: string;
  smtpSecure?: boolean | null;
  smtpUser?: string;
  smtpPassword?: string;
};

const defaultEmailSetting = {
  senderEmailId: 'no-reply@nxsys.tech',
  smtpHost: 'smtp.nxsys.tech',
  smtpPort: '587',
  smtpSecure: true,
  smtpUser: 'no-reply@nxsys.tech',
  smtpPassword: '********',
};

export type EmailSettingFormRef = {
  isValidForm: () => Promise<boolean>;
  getValues: () => EmailSettingValues;
  resetFields: () => void;
};

type Props = {
  formRef: Ref<EmailSettingFormRef>;
  initialValues: Partial<EmailSettingValues>;
  stepNumber: number;
  onEmailSettingTypeChange?: (type: EmailSettingType) => void;
};

const EmailSetting: React.FC<Props> = ({
  formRef,
  initialValues,
  stepNumber,
  onEmailSettingTypeChange,
}) => {
  const { isBureau } = useSelector((state: AppState) => state.appStore);
  const [form] = Form.useForm<EmailSettingValues>();
  const watchValues = Form.useWatch([], form);
  const selectedOption = watchValues?.emailSettingType;

  useImperativeHandle(formRef, () => ({
    isValidForm: async () => {
      try {
        await form.validateFields();
        return true;
      } catch (error) {
        return false;
      }
    },
    getValues() {
      return form.getFieldsValue(true);
    },
    resetFields() {
      form.resetFields();
    },
  }));

  useEffect(() => {
    if (selectedOption === 'DEFAULT') {
      form.setFieldsValue({
        ...defaultEmailSetting,
        emailSettingType: EmailSettingType.DEFAULT,
      });
    }
    if (selectedOption) {
      onEmailSettingTypeChange?.(selectedOption as EmailSettingType);
    }
  }, [selectedOption]);

  const onFinish: FormProps['onFinish'] = async (values) => {
    console.info('values', values);
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  const onChangeEmailSettingType = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === 'DEFAULT') {
      form.setFieldsValue({
        ...defaultEmailSetting,
        emailSettingType: EmailSettingType.DEFAULT,
      });
    } else {
      form.setFieldsValue({
        emailSettingType: EmailSettingType.CUSTOM,
        senderEmailId: '',
        smtpHost: '',
        smtpPort: '',
        smtpSecure: false,
        smtpUser: '',
        smtpPassword: '',
      });
    }
  };

  return (
    <div className="EmailSetting">
      <StepTitle
        step={stepNumber}
        title="Email Setting"
        description={isBureau ? 'Complete email setting for your employer!' : 'Complete email setting for your company!'}
      />

      <div className="text-[14px] text-primary-600 italic mt-[24px] mb-[24px]">
        *Choose which type of email setting below applies to you
      </div>

      <div className="space-y-[16px]">
        <AppFormContainer
          form={form}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          initialValues={{
            emailSettingType: 'DEFAULT',
            defaultEmailSetting,
            ...initialValues,
          }}
          layout="vertical"
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
        >
          <div className="mb-[16px]">
            <Form.Item name="emailSettingType">
              <AppRadioGroup
                className="flex flex-col gap-y-[12px]"
                onChange={onChangeEmailSettingType}
              >
                <div
                  className={clsx(
                    'border border-neutral-200 rounded-[12px] p-[16px]',
                    {
                      'border-primary-500': selectedOption === 'DEFAULT',
                    },
                  )}
                >
                  <AppRadioReversed value="DEFAULT">
                    <IconWrapper icon="IconEmail" />
                    <div className="flex-1 mt-[12px]">
                      <div className="text-[16px] font-medium mb-[8px]">
                        Use Nxsys's default email settings
                      </div>
                      <div className="text-[14px] text-neutral-600">
                        All correspondence to clients and employees will go from{' '}
                        <span className="text-primary-600">
                          no-reply@nxsys.tech
                        </span>
                      </div>
                    </div>
                  </AppRadioReversed>
                </div>

                <div
                  className={clsx(
                    'border border-neutral-200 rounded-[12px] p-[16px]',
                    {
                      'border-primary-500': selectedOption === 'CUSTOM',
                    },
                  )}
                >
                  <AppRadioReversed value="CUSTOM">
                    <IconWrapper icon="IconMailEdit" />
                    <div className="flex-1 mt-[12px]">
                      <div className="text-[16px] font-medium mb-[8px]">
                        Use custom email settings
                      </div>
                      <div className="text-[14px] text-neutral-600">
                        All correspondence to clients and employees will go from
                        your chosen email
                      </div>
                    </div>
                  </AppRadioReversed>
                </div>
              </AppRadioGroup>
            </Form.Item>
          </div>

          {selectedOption !== 'DEFAULT' && (
            <div className="mt-[24px]">
              <Row gutter={[16, 0]}>
                <Col span={12}>
                  <Form.Item
                    name="senderEmailId"
                    label="Sender Email ID"
                    rules={[
                      {
                        required: true,
                        message: 'Please enter sender email ID',
                      },
                      {
                        type: 'email',
                        message: 'Please enter a valid email address',
                      },
                    ]}
                  >
                    <AppInput
                      placeholder="Enter email id"
                      allowCapitalize={false}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="smtpHost"
                    label="SMTP Host"
                    rules={[
                      {
                        required: true,
                        message: 'Please enter SMTP host',
                      },
                    ]}
                  >
                    <AppInput
                      placeholder="Enter host"
                      allowCapitalize={false}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[16, 0]}>
                <Col span={12}>
                  <Form.Item
                    name="smtpPort"
                    label="SMTP Port"
                    rules={[
                      {
                        required: true,
                        message: 'Please enter SMTP port',
                      },
                    ]}
                  >
                    <AppRateInput placeholder="Enter port" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="smtpSecure" label="SMTP Secure">
                    <AppRadioGroup
                      options={yesNoOptions.map((option) => ({
                        label: option.label,
                        value: option.value,
                      }))}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[16, 0]}>
                <Col span={12}>
                  <Form.Item
                    name="smtpUser"
                    label="SMTP User"
                    rules={[
                      {
                        required: true,
                        message: 'Please enter SMTP user',
                      },
                    ]}
                  >
                    <AppInput
                      placeholder="Enter user"
                      allowCapitalize={false}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="smtpPassword"
                    label="Password"
                    rules={[
                      {
                        required: true,
                        message: 'Please enter password',
                      },
                    ]}
                  >
                    <AppInputPassword placeholder="Enter password" />
                  </Form.Item>
                </Col>
              </Row>
            </div>
          )}
        </AppFormContainer>
      </div>
    </div>
  );
};

export default EmailSetting;
